import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Navigate } from '@sorare/routing';
import {
  CraftClueBase,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Input } from '@sorare/core/src/atoms/inputs/Input';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  BodyM,
  HeadlineL,
  HeadlineS,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_ESSENCE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import type {
  CluesConversionPageQuery,
  CluesConversionPageQueryVariables,
  ConvertCraftCluesMutation,
  ConvertCraftCluesMutationVariables,
} from './__generated__/page.graphql.ts';

const RARITIES: Rarity[] = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

const RARITY_FACTORS: Record<Rarity, number> = {
  [Rarity.common]: 0,
  [Rarity.custom_series]: 0,
  [Rarity.limited]: 1,
  [Rarity.rare]: 2,
  [Rarity.super_rare]: 4,
  [Rarity.unique]: 8,
};

const messages = defineMessages({
  pageTitle: {
    defaultMessage: 'Football craft clues conversion',
  },
  pageIntro: {
    defaultMessage:
      'Rebalance your craft clues across rarities. Conversion uses the cost factors limited:1 / rare:2 / super rare:4 / unique:8 — you receive amount × fromFactor ÷ toFactor target clues.',
  },
  yourBalance: {
    defaultMessage: 'Your balance',
  },
  convert: {
    defaultMessage: 'Convert',
  },
  clueBase: {
    defaultMessage: 'Clue base',
  },
  fromRarity: {
    defaultMessage: 'From rarity',
  },
  toRarity: {
    defaultMessage: 'To rarity',
  },
  available: {
    defaultMessage: 'Available: {amount}',
  },
  current: {
    defaultMessage: 'Current: {amount}',
  },
  amountInputLabel: {
    defaultMessage: 'Amount of source clues to spend',
  },
  conversionPreview: {
    defaultMessage:
      'You will receive <strong>{targetAmount} {toRarity}</strong> {targetAmount, plural, one {clue} other {clues}} for <strong>{sourceAmount} {fromRarity}</strong> {sourceAmount, plural, one {clue} other {clues}}.',
  },
  notDivisible: {
    defaultMessage:
      'Amount must be a multiple of {multiple} to convert without a fractional remainder.',
  },
  insufficientBalance: {
    defaultMessage: 'Insufficient balance — you have {amount} {rarity} clues.',
  },
  confirmConversion: {
    defaultMessage: 'Confirm conversion',
  },
  baseCountry: {
    defaultMessage: 'Country',
  },
  baseCompetition: {
    defaultMessage: 'Competition',
  },
  baseFiftyFifty: {
    defaultMessage: '50/50',
  },
  baseBestStarRank: {
    defaultMessage: 'Best star rank',
  },
});

type BaseConfig = {
  value: CraftClueBase;
  label: MessageDescriptor;
  currencyKey: string;
  // Rarities for which this base actually has a clue currency. Some bases —
  // notably `country` — don't exist at all rarities (no unique country clue).
  availableRarities: Rarity[];
};

const BASES: BaseConfig[] = [
  {
    value: CraftClueBase.COUNTRY,
    label: messages.baseCountry,
    currencyKey: 'country_craft_clue',
    availableRarities: [Rarity.limited, Rarity.rare, Rarity.super_rare],
  },
  {
    value: CraftClueBase.COMPETITION,
    label: messages.baseCompetition,
    currencyKey: 'competition_craft_clue',
    availableRarities: RARITIES,
  },
  {
    value: CraftClueBase.FIFTY_FIFTY,
    label: messages.baseFiftyFifty,
    currencyKey: 'fifty_fifty_craft_clue',
    availableRarities: RARITIES,
  },
  {
    value: CraftClueBase.BEST_STAR_RANK,
    label: messages.baseBestStarRank,
    currencyKey: 'best_star_rank_craft_clue',
    availableRarities: RARITIES,
  },
];

const baseConfig = (base: CraftClueBase): BaseConfig =>
  BASES.find(b => b.value === base)!;

// Returns the InGameCurrency enum *string* for a (rarity, base) pair.
// We deliberately type as `string` rather than `InGameCurrency` so the page
// keeps working on environments where the rarity-specific football clue enum
// values haven't been deployed yet (proto repo follow-up from !30937).
const currencyFor = (rarity: Rarity, base: CraftClueBase): string =>
  `${rarity}_${baseConfig(base).currencyKey}`.toUpperCase();

const QUERY = gql`
  query CluesConversionPageQuery($sport: Sport!) {
    currentUser {
      slug
      inGameCurrencyBalances(sport: $sport) {
        currency
        amount
      }
    }
  }
` as TypedDocumentNode<
  CluesConversionPageQuery,
  CluesConversionPageQueryVariables
>;

const CONVERT_MUTATION = gql`
  mutation ConvertCraftCluesMutation($input: convertCraftCluesInput!) {
    convertCraftClues(input: $input) {
      currentUser {
        slug
        inGameCurrencyBalances(sport: FOOTBALL) {
          currency
          amount
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  ConvertCraftCluesMutation,
  ConvertCraftCluesMutationVariables
>;

const Card = styled(Vertical)`
  background: var(--c-nd-50);
  border-radius: var(--double-unit);
  padding: var(--triple-unit);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 1fr) repeat(4, minmax(80px, 1fr));
  gap: var(--unit) var(--double-unit);
  align-items: center;
`;

type Balances = Record<string, number>;

const useBalances = (data: CluesConversionPageQuery | undefined): Balances => {
  return useMemo(() => {
    const result: Balances = {};
    data?.currentUser?.inGameCurrencyBalances.forEach(b => {
      result[b.currency] = b.amount;
    });
    return result;
  }, [data]);
};

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

const BaseRow = ({
  base,
  balances,
}: {
  base: (typeof BASES)[number];
  balances: Balances;
}) => (
  <>
    <LabelM bold>
      <FormattedMessage {...base.label} />
    </LabelM>
    {RARITIES.map(r => {
      if (!base.availableRarities.includes(r)) {
        return (
          <div key={r} className="text-right">
            <LabelM color="var(--c-nd-600)">—</LabelM>
          </div>
        );
      }
      return (
        <div key={r} className="text-right">
          <LabelM>{balances[currencyFor(r, base.value)] ?? 0}</LabelM>
        </div>
      );
    })}
  </>
);

const Page = () => {
  const {
    flags: { craftCluesConversion = false },
  } = useFeatureFlags();
  const { generateSportContextPath } = useSportContext();
  const { formatMessage } = useIntl();

  const { data, loading } = useQuery(QUERY, {
    variables: { sport: Sport.FOOTBALL },
    skip: !craftCluesConversion,
  });
  const balances = useBalances(data);

  const [base, setBase] = useState<CraftClueBase>(CraftClueBase.COUNTRY);
  const [fromRarity, setFromRarity] = useState<Rarity>(Rarity.limited);
  const [toRarity, setToRarity] = useState<Rarity>(Rarity.rare);
  const [amount, setAmount] = useState<string>('');

  const [mutate, { loading: submitting }] = useMutation(CONVERT_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  if (!craftCluesConversion) {
    return (
      <Navigate to={generateSportContextPath(ANY_SPORT_ESSENCE)} replace />
    );
  }

  const onPickBase = (b: CraftClueBase) => {
    setBase(b);
    setAmount('');
    const allowed = baseConfig(b).availableRarities;
    if (!allowed.includes(fromRarity)) setFromRarity(allowed[0]);
    if (!allowed.includes(toRarity)) {
      const fallback = allowed.find(r => r !== fromRarity) ?? allowed[0];
      setToRarity(fallback);
    }
  };

  const onPickFromRarity = (r: Rarity) => {
    setFromRarity(r);
    setAmount('');
    if (r === toRarity) {
      const allowed = baseConfig(base).availableRarities;
      const next = allowed.find(other => other !== r);
      if (next) setToRarity(next);
    }
  };

  const onPickToRarity = (r: Rarity) => {
    setToRarity(r);
    setAmount('');
  };

  const fromFactor = RARITY_FACTORS[fromRarity] || 1;
  const toFactor = RARITY_FACTORS[toRarity] || 1;
  const numericAmount = Number.parseInt(amount, 10);
  const isAmountValid = Number.isFinite(numericAmount) && numericAmount > 0;
  const targetAmountTimesFactor = isAmountValid
    ? numericAmount * fromFactor
    : 0;
  const isDivisible = isAmountValid && targetAmountTimesFactor % toFactor === 0;
  const targetAmount = isDivisible ? targetAmountTimesFactor / toFactor : 0;
  const fromCurrency = currencyFor(fromRarity, base);
  const toCurrency = currencyFor(toRarity, base);
  const fromBalance = balances[fromCurrency] ?? 0;
  const hasBalance = isAmountValid && numericAmount <= fromBalance;
  const sameRarity = fromRarity === toRarity;

  const canSubmit = isAmountValid && isDivisible && hasBalance && !sameRarity;

  const onConfirm = async () => {
    if (!canSubmit) return;
    const result = await mutate({
      variables: {
        input: {
          amount: numericAmount,
          clueBaseName: base,
          fromRarity,
          toRarity,
        },
      },
    });
    if (result.data?.convertCraftClues?.errors.length === 0) {
      setAmount('');
    }
  };

  if (loading && !data) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <Container>
      <Vertical gap={4} style={{ padding: 'var(--quadruple-unit) 0' }}>
        <Vertical gap={1}>
          <HeadlineL>
            <FormattedMessage {...messages.pageTitle} />
          </HeadlineL>
          <BodyM color="var(--c-nd-600)">
            <FormattedMessage {...messages.pageIntro} />
          </BodyM>
        </Vertical>

        <Card gap={2}>
          <HeadlineS>
            <FormattedMessage {...messages.yourBalance} />
          </HeadlineS>
          <Grid>
            <div />
            {RARITIES.map(r => (
              <div key={r} className="text-right">
                <LabelS bold>
                  <FormattedMessage {...scarcityMessages[r]} />
                </LabelS>
              </div>
            ))}
            {BASES.map(b => (
              <BaseRow key={b.value} base={b} balances={balances} />
            ))}
          </Grid>
        </Card>

        <Card gap={3}>
          <HeadlineS>
            <FormattedMessage {...messages.convert} />
          </HeadlineS>

          <Vertical gap={1}>
            <LabelM bold>
              <FormattedMessage {...messages.clueBase} />
            </LabelM>
            <Horizontal gap={1}>
              {BASES.map(b => (
                <Button
                  key={b.value}
                  size="small"
                  color={base === b.value ? 'primary' : 'tertiary'}
                  onClick={() => onPickBase(b.value)}
                >
                  <FormattedMessage {...b.label} />
                </Button>
              ))}
            </Horizontal>
          </Vertical>

          <Horizontal gap={3}>
            <Vertical gap={1} style={{ flex: 1 }}>
              <LabelM bold>
                <FormattedMessage {...messages.fromRarity} />
              </LabelM>
              <Horizontal gap={1}>
                {RARITIES.map(r => (
                  <Button
                    key={r}
                    size="small"
                    color={fromRarity === r ? 'primary' : 'tertiary'}
                    disabled={!baseConfig(base).availableRarities.includes(r)}
                    onClick={() => onPickFromRarity(r)}
                  >
                    <FormattedMessage {...scarcityMessages[r]} />
                  </Button>
                ))}
              </Horizontal>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage
                  {...messages.available}
                  values={{ amount: fromBalance }}
                />
              </LabelS>
            </Vertical>

            <Vertical gap={1} style={{ flex: 1 }}>
              <LabelM bold>
                <FormattedMessage {...messages.toRarity} />
              </LabelM>
              <Horizontal gap={1}>
                {RARITIES.map(r => (
                  <Button
                    key={r}
                    size="small"
                    color={toRarity === r ? 'primary' : 'tertiary'}
                    disabled={
                      r === fromRarity ||
                      !baseConfig(base).availableRarities.includes(r)
                    }
                    onClick={() => onPickToRarity(r)}
                  >
                    <FormattedMessage {...scarcityMessages[r]} />
                  </Button>
                ))}
              </Horizontal>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage
                  {...messages.current}
                  values={{ amount: balances[toCurrency] ?? 0 }}
                />
              </LabelS>
            </Vertical>
          </Horizontal>

          <Vertical gap={1}>
            <LabelM bold>
              <FormattedMessage {...messages.amountInputLabel} />
            </LabelM>
            <Input
              type="number"
              min={1}
              max={fromBalance}
              step={1}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0"
            />
          </Vertical>

          <Vertical gap={0}>
            <BodyM>
              <FormattedMessage
                {...messages.conversionPreview}
                values={{
                  targetAmount,
                  sourceAmount: isAmountValid ? numericAmount : 0,
                  toRarity: formatMessage(scarcityMessages[toRarity]),
                  fromRarity: formatMessage(scarcityMessages[fromRarity]),
                  strong: chunks => <strong>{chunks}</strong>,
                }}
              />
            </BodyM>
            {!!isAmountValid && !isDivisible && (
              <LabelS color="var(--c-red-600)">
                <FormattedMessage
                  {...messages.notDivisible}
                  values={{ multiple: toFactor / gcd(fromFactor, toFactor) }}
                />
              </LabelS>
            )}
            {!!isAmountValid && !!isDivisible && !hasBalance && (
              <LabelS color="var(--c-red-600)">
                <FormattedMessage
                  {...messages.insufficientBalance}
                  values={{
                    amount: fromBalance,
                    rarity: formatMessage(scarcityMessages[fromRarity]),
                  }}
                />
              </LabelS>
            )}
          </Vertical>

          <Horizontal>
            <LoadingButton
              color="primary"
              size="medium"
              loading={submitting}
              disabled={!canSubmit}
              onClick={onConfirm}
            >
              <FormattedMessage {...messages.confirmConversion} />
            </LoadingButton>
          </Horizontal>
        </Card>
      </Vertical>
    </Container>
  );
};

export default Page;
